/**
 * Manage → Data events.
 *
 * The workspace event feed (`data_event`, `docs/data-loop-plan.md` §2.4) as an
 * operator's screen. The canvas and the apps read the same table live and
 * oldest-first ("what happened after X"); this page reads it newest-first and
 * across workspaces ("what has the loop been doing"), because the question an
 * admin brings is the opposite of the one a binding brings.
 *
 * It is a record, not a control panel: nothing here can be edited or removed.
 * The objects those events are about — files, tables, drafts — live on
 * "Made by the platform", which is where an undo happens.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { type ComponentProps, useState } from "react";

import { ApiError } from "@chai/api-client";

import { useApi } from "@/api";
import { PageHeader } from "@/components/manage/PageHeader";
import { EmptyRow, Section } from "@/components/manage/Section";
import { StatStrip } from "@/components/manage/StatStrip";
import { StatusBadge } from "@/components/manage/StatusBadge";
import { useIsDemoManage } from "@/components/manage/org-context";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/manage/data-events")({
  component: DataEventsPage,
});

interface DataEvent {
  id: string;
  workspace_id: string;
  kind: string;
  subject: string;
  payload: Record<string, unknown>;
  created_at: string | null;
}

interface KindCount {
  kind: string;
  count: number;
}

interface WorkspaceCount {
  workspace_id: string;
  count: number;
  latest_at: string | null;
}

interface DataEvents {
  events: DataEvent[];
  next_before: string | null;
  kinds: KindCount[];
  workspaces: WorkspaceCount[];
  vocabulary: string[];
}

type Tone = NonNullable<ComponentProps<typeof StatusBadge>["tone"]>;

/** Radix Select refuses an empty item value, so "everything" needs a word. */
const ALL = "__all__";
const PAGE = 100;

const TH =
  "px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground";

const KIND_TONE: Record<string, Tone> = {
  "dataset.synced": "ready",
  "table.published": "ready",
  "artifact.created": "info",
  "run.started": "pending",
  "run.finished": "ready",
  "webhook.delivered": "info",
  "cron.finished": "neutral",
  "file.ingested": "info",
  "file.created": "info",
  "mail.drafted": "pending",
  "mail.sent": "ready",
};

/** A finished run or a delivery that failed says so in its payload; the row
 *  should read as a failure before anyone opens it. */
function toneFor(event: DataEvent): Tone {
  const p = event.payload;
  const status = typeof p.status === "string" ? p.status : "";
  if (p.ok === false || status === "failed" || status === "error") return "failed";
  return KIND_TONE[event.kind] ?? "neutral";
}

function when(value: string | null): string {
  if (!value) return "—";
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? "—" : at.toLocaleString();
}

function short(value: unknown): string {
  const text = typeof value === "string" ? value : JSON.stringify(value);
  if (text === undefined) return "";
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
}

/** "rows: 3 · table: rollup_daily" — the first few keys, so the row says
 *  what happened without a click. The full payload sits behind it. */
function summary(payload: Record<string, unknown>): string {
  const entries = Object.entries(payload);
  if (!entries.length) return "";
  const shown = entries.slice(0, 4).map(([k, v]) => `${k}: ${short(v)}`);
  return entries.length > 4 ? `${shown.join(" · ")} · …` : shown.join(" · ");
}

function DataEventsPage() {
  const api = useApi();
  const isDemo = useIsDemoManage();
  const [workspace, setWorkspace] = useState(ALL);
  const [kind, setKind] = useState(ALL);

  const feed = useInfiniteQuery({
    queryKey: ["admin-data-events", workspace, kind] as const,
    enabled: !isDemo,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE) });
      if (workspace !== ALL) params.set("workspace_id", workspace);
      if (kind !== ALL) params.set("kind", kind);
      if (pageParam) params.set("before", pageParam);
      return api.request<DataEvents>(`/admin/data-events?${params.toString()}`);
    },
    getNextPageParam: (last) => last.next_before,
    refetchInterval: 20_000,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });

  if (isDemo) {
    return (
      <div className="mx-auto max-w-[1100px] px-10 py-10 text-sm text-muted-foreground">
        Not available in this demo.
      </div>
    );
  }

  const first = feed.data?.pages[0];
  const events = feed.data?.pages.flatMap((p) => p.events) ?? [];
  const kinds = first?.kinds ?? [];
  const workspaces = first?.workspaces ?? [];
  const vocabulary = first?.vocabulary ?? [];
  const total = kinds.reduce((sum, k) => sum + k.count, 0);
  const latest = events[0];
  const refusal =
    feed.isError && feed.error instanceof ApiError && feed.error.status < 500
      ? feed.error.message
      : null;

  return (
    <div className="mx-auto max-w-[1100px] px-10 py-10">
      <PageHeader
        eyebrow="Admin · Data loop"
        title="Data events"
        description="Everything the loop recorded: datasets synced, tables published, runs, deliveries, files and mail — newest first, across every workspace."
        actions={
          <div className="flex items-center gap-2">
            <Select value={workspace} onValueChange={(v) => setWorkspace(v ?? ALL)}>
              <SelectTrigger className="w-[220px]" aria-label="Workspace">
                <SelectValue>{workspace === ALL ? "All workspaces" : workspace}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All workspaces</SelectItem>
                {workspaces.map((w) => (
                  <SelectItem key={w.workspace_id} value={w.workspace_id}>
                    {w.workspace_id} · {w.count}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={kind} onValueChange={(v) => setKind(v ?? ALL)}>
              <SelectTrigger className="w-[180px]" aria-label="Kind">
                <SelectValue>{kind === ALL ? "All kinds" : kind}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All kinds</SelectItem>
                {vocabulary.map((k) => (
                  <SelectItem key={k} value={k}>
                    {k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void feed.refetch()}
              disabled={feed.isFetching}
              aria-label="Refresh"
            >
              <RefreshCw className={feed.isFetching ? "size-3.5 animate-spin" : "size-3.5"} />
            </Button>
          </div>
        }
      />

      {refusal ? (
        <p className="text-sm text-muted-foreground">{refusal}</p>
      ) : (
        <>
          <StatStrip
            items={[
              {
                label: "Events",
                value: total,
                hint: workspace === ALL ? "across all workspaces" : "in this workspace",
              },
              {
                label: "Kinds seen",
                value: `${kinds.length} / ${vocabulary.length || "—"}`,
                hint: "of the closed vocabulary",
              },
              { label: "Workspaces", value: workspaces.length, hint: "that have emitted" },
              {
                label: "Latest",
                value: latest ? when(latest.created_at) : "—",
                hint: latest ? latest.kind : "nothing yet",
              },
            ]}
          />

          {kinds.length ? (
            <div className="mb-8 flex flex-wrap items-center gap-2">
              {kinds.map((k) => (
                <button
                  key={k.kind}
                  type="button"
                  className="rounded-full transition hover:opacity-80"
                  onClick={() => setKind(kind === k.kind ? ALL : k.kind)}
                  aria-pressed={kind === k.kind}
                  title={kind === k.kind ? "Show every kind" : `Only ${k.kind}`}
                >
                  <StatusBadge
                    label={`${k.kind} × ${k.count}`}
                    tone={KIND_TONE[k.kind] ?? "neutral"}
                    className={kind === k.kind ? "ring-1 ring-[var(--color-charcoal)]" : ""}
                  />
                </button>
              ))}
            </div>
          ) : null}

          <Section
            title="Feed"
            description="One row per fact. Open a row for the full payload the emitter recorded."
          >
            {feed.isLoading ? (
              <Skeleton className="h-40 w-full rounded-md" />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <th className={TH}>When</th>
                      <th className={TH}>Kind</th>
                      <th className={TH}>Subject</th>
                      <th className={TH}>Workspace</th>
                      <th className={TH}>Payload</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {events.length ? (
                      events.map((row) => (
                        <tr
                          key={row.id}
                          className="align-top transition hover:bg-[var(--color-surface)]"
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                            {when(row.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge label={row.kind} tone={toneFor(row)} />
                          </td>
                          <td className="px-4 py-3 font-medium text-foreground">
                            {row.subject || <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            {row.workspace_id}
                          </td>
                          <td className="max-w-[380px] break-words px-4 py-3 text-xs text-muted-foreground">
                            {Object.keys(row.payload).length ? (
                              <details>
                                <summary className="cursor-pointer list-none">
                                  {summary(row.payload)}
                                </summary>
                                <pre className="mt-2 max-h-64 overflow-auto rounded bg-[var(--color-surface-soft)] p-2 font-mono text-[11px] leading-snug text-foreground">
                                  {JSON.stringify(row.payload, null, 2)}
                                </pre>
                              </details>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <EmptyRow colSpan={5}>
                        Nothing yet — no dataset has synced, no table has been published and no run
                        has finished under this filter.
                      </EmptyRow>
                    )}
                  </tbody>
                </table>
                {feed.hasNextPage ? (
                  <div className="border-t border-[var(--color-hairline-soft)] px-4 py-3">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={feed.isFetchingNextPage}
                      onClick={() => void feed.fetchNextPage()}
                    >
                      {feed.isFetchingNextPage ? "Loading…" : "Older events"}
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
